﻿<script lang="ts">
    import { Link, NavRight, Navbar, Page, NavLeft, f7 } from 'framework7-svelte';
    import 'leaflet/dist/leaflet.css';
    import type { LatLng, LatLngExpression } from 'leaflet';
    import {createEventDispatcher} from 'svelte';
    import { _ } from 'svelte-i18n';
    
    import logo from '../../assets/lisa-buko.svg';
    import RouteEditor from './RouteEditor.svelte';
    import RouteEditorMap from './RouteEditorMap.svelte';
    import { notify } from '../../utils/notify';
    import type { Toast } from 'framework7/types';
    import type {RouteEditorRoute} from "../../models/route-editor-route.model";
    import type {RouteEditorGate} from "../../models/route-editor-gate.model";
    import type {ProjectMapRequest} from "../../models/project-map-request.model";
    import {mapGateToRouteEditorGate, mapRouteToRouteEditorRoute} from "../../helpers/route-manager.helper";
    import {projectStore} from "../../stores/project.store";
    import type {Project} from "../../types/project.type";
    import type {Gate} from "../../types/projectItems/gate.type";
    import {putProjectMap} from "../../api/projects.api";
    
    const eventDispatcher = createEventDispatcher();

    let isSaving = false;
    let changed = false;
    
    let routesByName: Map<string, RouteEditorRoute>;
    let gatesByName: Map<string, RouteEditorGate>;
    
    let selectedRoute: RouteEditorRoute | null;
    let selectedGate: RouteEditorGate | null;
    let selectedGateIndex: number | null;
    
    let highlightedGate: RouteEditorGate | null;
    let isAddingGate = false;

    let onLatLng: (value: LatLng) => void;
    let onGateSelected: (id: string) => void;

    let gateToast: Toast.Toast;

    const VORMER_ZWOLLE: LatLngExpression = [52.50846510314036, 6.123970270747168];
    let mapCenter = VORMER_ZWOLLE; // @TODO: is this ok?
    
    $: mapProjectToRouteEditor($projectStore.data)
    $: gates = Array.from(gatesByName?.values() ?? []);
    $: routes = Array.from(routesByName?.values() ?? []);
    
    function mapProjectToRouteEditor(project: Project) {
        gatesByName = new Map<string, RouteEditorGate>(
            project.gates.map(
                gate => [gate.name, mapGateToRouteEditorGate(gate)]
            )
        );

        const gatesById = new Map<string, Gate>(
            project.gates.map(gate => [gate.id, gate])
        );

        routesByName = new Map<string, RouteEditorRoute>(
            project.routes.map(
                route => [route.name, mapRouteToRouteEditorRoute(route, gatesById)]
            )
        );
    }

    async function saveInsight() {
        const routes = [...routesByName.values()];
        const routesWithoutGates = routes.filter(route => route.gateNames.length < 1);
        if(routesWithoutGates.length > 0){
            const names = routesWithoutGates.map(route => route.name).join(', ');
            notify($_('pages.route-editor.ROUTES_HAVE_NO_GATES', { values: { names } }));
            return;
        }
        
        isSaving = true;
        const requestBody: ProjectMapRequest = {
            routes: [...routesByName.values()],
            gates: [...gatesByName.values()]
        }

        await putProjectMap($projectStore.data.id, requestBody);

        changed = false;
        isSaving = false;
        
        await handleClose(null, true);
    }

    function handleLatLng(latlngEvent) {
        if (onLatLng) {
            const handler = onLatLng;
            onLatLng = undefined;
            onGateSelected = undefined;
            handler(latlngEvent.detail);
        }
    }

    function handleGateSelected(gateEvent) {
        if (onGateSelected) {
            const handler = onGateSelected;
            onLatLng = undefined;
            onGateSelected = undefined;
            handler(gateEvent.detail);
        }
    }

    async function handleClose(event: CustomEvent, closedFromSave = false) {
        if (changed) {
            f7.dialog.confirm(
                $_('pages.route-editor.CONTINUE_ON_UNSAVED_CHANGES'),
                $_('pages.route-editor.CONTINUE_WITHOUT_SAVING'), 
                () => f7.popup.close()
            );
        }
        else if (closedFromSave) {
            f7.dialog.confirm(
                $_('pages.route-editor.RETURN_TO_MAP_ON_SAVE'),
                '',
                () => {
                    f7.preloader.show();

                    window.setTimeout(() => {
                        eventDispatcher('insightUpdated');
                        f7.preloader.hide();
                        f7.popup.close();
                    }, 2000);
                }
            );
        }
        else {
            eventDispatcher('insightUpdated');
            f7.popup.close();
        }
    }

    function markChanged() {
        changed = true;
    }

    function handleDeleteRoute(routeToDelete: RouteEditorRoute) {
        f7.dialog.confirm(
            $_('pages.route-editor.CONFIRM_DELETE_ROUTE', { values: { routeName: routeToDelete.name } }),
            $_('shared.DELETE') + '?', () => {
                routesByName.get(routeToDelete.name)?.gateNames.forEach(gateName => {
                    const isOnlyRouteForGate = routes.filter((route) =>
                        route.gateNames.includes(gateName)
                    ).length === 1;
                    if (isOnlyRouteForGate) {
                        gatesByName.delete(gateName);
                    }
                })
                routesByName.delete(routeToDelete.name);
                
                // reassignment for svelte reactivity
                routesByName = routesByName;
                gatesByName = gatesByName;
                
                clearSelectedRoute();
                markChanged();
            }
        );
    }

    function handleDeleteGateFromRoute(selectedGate: RouteEditorGate, selectedRoute: RouteEditorRoute, selectedGateIndex: number | null) {
        const isOnlyRouteForGate = routes.filter((route) => 
            route.gateNames.includes(selectedGate.name)
        ).length === 1;
        
        f7.dialog.confirm(
            $_('pages.route-editor.CONFIRM_DELETE_GATE') + (isOnlyRouteForGate
                ? ' ' +  $_('pages.route-editor.GATE_WILL_BE_DELETED_WARNING')
                : ''),
            $_('shared.DELETE') + '?',
            () => {
                selectedRoute.gateNames.splice(selectedGateIndex, 1);
                
                // reassignment for svelte reactivity
                selectedRoute = selectedRoute;
                routesByName = routesByName;
                if (isOnlyRouteForGate) {
                    gatesByName.delete(selectedGate.name);
                    gatesByName = gatesByName;
                }

                clearSelectedGate();
                markChanged();
            }
        );
    }

    function handleEditRouteName(selectedRoute: RouteEditorRoute) {
        const nameBeforeEdit = selectedRoute.name;
        f7.dialog.prompt(
            $_('pages.route-editor.EDIT_ROUTE_NAME'),
            'Route ' + selectedRoute.name,
            (name) => {
                const newNameTrimmed = trimIfOverLength(name, 100);
                if (nameBeforeEdit !== newNameTrimmed && routesByName.has(newNameTrimmed)) {
                    notify($_('pages.route-editor.ROUTE_WITH_NAME_ALREADY_EXISTS'));
                    return;
                }
                selectedRoute.name = newNameTrimmed;
                
                // reassignment for svelte reactivity
                selectedRoute = selectedRoute;
                routesByName = routesByName;
                markChanged();
            },
            () => {},
            selectedRoute.name
        );
    }

    function handleAddRoute() {
        f7.dialog.prompt(
            $_('pages.route-editor.NEW_ROUTE_NAME'),
            $_('pages.route-editor.NEW_ROUTE'), 
            (name) => {
                const nameTrimmed = trimIfOverLength(name, 100);
                if (routesByName.has(nameTrimmed)) {
                    notify($_('pages.route-editor.ROUTE_WITH_NAME_ALREADY_EXISTS'));
                    return;
                }

                if (nameTrimmed.length === 0) {
                    notify($_('pages.route-editor.ROUTE_MUST_HAVE_NAME'));
                    isAddingGate = false;
                    return;
                }
    
                const newRoute: RouteEditorRoute = {
                    id: null,
                    name: nameTrimmed,
                    gateNames: []
                };
                
                routesByName.set(nameTrimmed, newRoute);
                // reassignment for svelte reactivity
                routesByName = routesByName;
                
                clearSelectedRoute();
                setSelectedRoute(newRoute);
                notify($_('pages.route-editor.ROUTE_ADDED', { values: { name: nameTrimmed }}));
                markChanged();
            }
        );
    }

    function handleEditGateName(selectedGate: RouteEditorGate) {
        const nameBeforeEdit = selectedGate.name;
        
        f7.dialog.prompt(
            $_('pages.route-editor.EDIT_GATE_NAME'),
            'Gate ' + selectedGate.name,
            (newName) => {
                const newNameTrimmed = trimIfOverLength(newName, 100);
                if (nameBeforeEdit !== newNameTrimmed && gatesByName.has(newNameTrimmed)) {
                    notify($_('pages.route-editor.GATE_WITH_NAME_ALREADY_EXISTS'));
                    return;
                }
                
                routesByName.forEach(route => {
                    route.gateNames = route.gateNames.map(gateName => gateName === nameBeforeEdit ? newNameTrimmed : gateName);
                })
                selectedGate.name = newNameTrimmed;

                gatesByName.delete(nameBeforeEdit);
                gatesByName.set(newNameTrimmed, selectedGate);
                
                // reassignment for svelte reactivity
                selectedGate = selectedGate;
                gatesByName = gatesByName;
                routesByName = routesByName;

                markChanged();
            },
            () => {},
            selectedGate.name
        );
    }

    function handleAddGate() {
        isAddingGate = true;
        if (!selectedRoute) {
            return;
        }

        onLatLng = (latlng) => {
            gateToast.close();
            f7.dialog.prompt(
                $_('pages.route-editor.NEW_GATE_NAME'),
                $_('pages.route-editor.NEW_GATE'),
                (name) => {
                    const nameTrimmed = trimIfOverLength(name, 100);
                    
                    if (gatesByName.has(nameTrimmed)) {
                        notify($_('pages.route-editor.GATE_WITH_NAME_ALREADY_EXISTS'));
                        isAddingGate = false;
                        return;
                    }

                    if (nameTrimmed.length === 0) {
                        notify($_('pages.route-editor.GATE_MUST_HAVE_NAME'));
                        isAddingGate = false;
                        return;
                    }
                    
                    const newGate: RouteEditorGate = {
                        id: null,
                        name: nameTrimmed,
                        latitude: latlng.lat,
                        longitude: latlng.lng
                    };
                    gatesByName.set(nameTrimmed, newGate);
                    // reassignment for svelte reactivity
                    gatesByName = gatesByName;
                    
                    if (selectedGate && selectedRoute) {
                        const idx = selectedRoute.gateNames.indexOf(selectedGate.name);
                        selectedRoute.gateNames.splice(idx + 1, 0, nameTrimmed);
                    } else {
                        selectedRoute.gateNames.push(nameTrimmed);
                    }
                    
                    notify($_('pages.route-editor.GATE_ADDED', { values: { name: nameTrimmed, routeName: selectedRoute.name }}));
                    
                    setSelectedGate(newGate, selectedRoute.gateNames.indexOf(nameTrimmed));
                    routesByName = routesByName;

                    markChanged();
                    isAddingGate = false;
                },
                () => {
                    isAddingGate = false;
                }
            );
        };

        onGateSelected = (name) => {
            gateToast.close();
            if (selectedGate && selectedRoute) {
                const idx = selectedRoute.gateNames.indexOf(selectedGate.name);
                selectedRoute.gateNames.splice(idx + 1, 0, name);
            } else {
                selectedRoute.gateNames.push(name);
            }
            
            setSelectedGate(gatesByName.get(name), selectedRoute.gateNames.indexOf(name));
            // reassignment for svelte reactivity
            gatesByName = gatesByName;
            
            markChanged();
            isAddingGate = false;
        };

        if (!gateToast) {
            gateToast = f7.toast.create({
                position: 'bottom',
                closeButton: true,
                horizontalPosition: 'center',
                closeButtonText: $_('shared.CANCEL'),
                text: $_('pages.route-editor.PLACE_GATE_ON_MAP'),
                on: {
                    close() {
                        if (isAddingGate) {
                            isAddingGate = false;
                        }
                    }
                }
            });
        }

        gateToast.open();
    }

    function handleEditGateLocation(gate: RouteEditorGate) {
        isAddingGate = true;
        if (!selectedRoute) {
            return;
        }

        onLatLng = (latlng) => {
            gateToast.close();
            gate.latitude = latlng.lat;
            gate.longitude = latlng.lng;

            // reassignment for svelte reactivity
            gatesByName = gatesByName;
            markChanged();
            isAddingGate = false;
        };

        onGateSelected = (name) => {
            gateToast.close();
            const existingGate = gatesByName.get(name)
            if (existingGate) {
                gate = {
                    ...gate,
                    latitude: existingGate.latitude,
                    longitude:existingGate.longitude
                };

                // reassignment for svelte reactivity
                gatesByName = gatesByName;
                markChanged();
            }
            
            isAddingGate = false;
        };

        if (!gateToast) {
            gateToast = f7.toast.create({
                position: 'bottom',
                closeButton: true,
                horizontalPosition: 'center',
                closeButtonText: $_('shared.CANCEL'),
                text: $_('pages.route-editor.PLACE_GATE_ON_MAP'),
                on: {
                    close() {
                        if (isAddingGate) {
                            isAddingGate = false;
                        }
                    }
                }
            });
        }

        gateToast.open();
    }
    
    function setSelectedRoute(route: RouteEditorRoute, gate: RouteEditorGate = null, gateIndex: number = null) {
        selectedRoute = route;
        if(gate != null && gateIndex != null)
            setSelectedGate(gate, gateIndex);
    }
    
    function setSelectedGate(gate: RouteEditorGate, gateIndex: number) {
        selectedGate = gate;
        selectedGateIndex = gateIndex;
    }
    
    function clearSelectedRoute() {
        selectedRoute = null;
        clearSelectedGate()
    }
    
    function clearSelectedGate() {
        selectedGate = null;
        selectedGateIndex = null;
    }

    function trimIfOverLength(input: string, maxLength: number): string {
        return input.length > maxLength ? input.slice(0, maxLength - 1) : input;
    }
</script>

<Page>
    <Navbar>
        <NavLeft>
            <Link href="/">
                <img src={logo} alt="Traffic Life Logo" />
            </Link>
            <span class="project-name">
                {$projectStore.data.name}
            </span>
        </NavLeft>
        <NavRight>
            <Link on:click={handleClose}>{$_('shared.CLOSE')}</Link>
        </NavRight>
    </Navbar>

    <div class="main-container">
        <div class="map-container">
            <RouteEditorMap
                center={mapCenter}
                {gates}
                {routes}
                {selectedRoute}
                {selectedGate}
                {highlightedGate}
                {isAddingGate}
                {gatesByName}
                on:latlng={handleLatLng}
                on:gate={handleGateSelected}
            />
        </div>

        <div class="editor-container">
            <RouteEditor
                bind:routes
                bind:gates
                bind:selectedRoute
                bind:selectedGate
                bind:selectedGateIndex
                bind:highlightedGate
                {gatesByName}
                {isSaving}
                hasChanges={changed}
                {isAddingGate}
                on:addRoute={handleAddRoute}
                on:addGate={handleAddGate}
                on:save={saveInsight}
                on:changed={markChanged}
                on:close={handleClose}
                on:editRouteName={() => handleEditRouteName(selectedRoute)}
                on:editGateName={() => handleEditGateName(selectedGate)}
                on:deleteRoute={() => handleDeleteRoute(selectedRoute)}
                on:deleteGate={() => handleDeleteGateFromRoute(selectedGate, selectedRoute, selectedGateIndex)}
                on:editGateLocation={() => handleEditGateLocation(selectedGate)}
            />
        </div>
    </div>
</Page>c

<style lang="scss">
    @import '../../assets/scss/colors';

    .main-container {
        width: 100%;
        height: 100%;
        display: flex;
        flex-direction: row;
    }

    .map-container {
        flex-grow: 6;
    }

    .editor-container {
        width: 376px;
        padding: 12px 12px 0 12px;
        box-shadow: 1px 1px 3px #{$box-shadow-color};
        background: var(--f7-card-bg-color);
        z-index: 801;
    }
</style>
